<template>
  <div class="flex flex-col">
    <h2>Programa de Referidos</h2>

    <div v-if="!loading" class="card mt-2 max-w-2xl">
      <form class="flex flex-col" @submit.prevent="save">
        <div class="flex flex-col mt-4">
          <label for="referrer_percent">% para quien refiere</label>
          <p class="text-sm text-content-400">
            Porcentaje del primer pago del referido que se abona al monedero de
            quien lo refirió.
          </p>
          <input
            id="referrer_percent"
            type="number"
            step="0.01"
            min="0"
            max="100"
            v-model.number="form.referrer_percent"
          />
          <validation-errors
            :errors="errorFor('referrer_percent')"
          ></validation-errors>
        </div>

        <div class="flex flex-col mt-4">
          <label for="referred_percent">% de descuento de bienvenida</label>
          <p class="text-sm text-content-400">
            Descuento del cupón de bienvenida para el cliente referido (solo
            primera compra).
          </p>
          <input
            id="referred_percent"
            type="number"
            step="0.01"
            min="0"
            max="100"
            v-model.number="form.referred_percent"
          />
          <validation-errors
            :errors="errorFor('referred_percent')"
          ></validation-errors>
        </div>

        <div class="flex flex-col mt-4">
          <label for="welcome_days">Vigencia del cupón de bienvenida (días)</label>
          <p class="text-sm text-content-400">
            Días de validez del cupón a partir de que el referido recibe su
            cotización.
          </p>
          <input
            id="welcome_days"
            type="number"
            min="1"
            max="365"
            v-model.number="form.welcome_days"
          />
          <validation-errors :errors="errorFor('welcome_days')"></validation-errors>
        </div>

        <div class="flex flex-col mt-4">
          <label for="wallet_expiry_months">Vigencia del saldo del monedero (meses)</label>
          <p class="text-sm text-content-400">
            Cada abono al monedero expira este número de meses después de
            generarse.
          </p>
          <input
            id="wallet_expiry_months"
            type="number"
            min="1"
            max="60"
            v-model.number="form.wallet_expiry_months"
          />
          <validation-errors
            :errors="errorFor('wallet_expiry_months')"
          ></validation-errors>
        </div>

        <div class="flex flex-col mt-4">
          <label>Frecuencias con descuento de bienvenida</label>
          <p class="text-sm text-content-400">
            Frecuencias de pago en las que el cupón de bienvenida del referido
            es válido.
          </p>
          <div class="mt-2 space-y-2">
            <div class="flex items-center">
              <input
                id="all_payment_frequencies"
                type="checkbox"
                class="h-5 w-5"
                :checked="allPaymentFrequencies"
                :disabled="allPaymentFrequencies"
                @change="form.allowed_payment_frequency_ids = []"
              />
              <label for="all_payment_frequencies" class="ml-2"
                >Todas las frecuencias</label
              >
            </div>
            <div
              v-for="frequency in selectablePaymentFrequencies"
              :key="frequency.id"
              class="flex items-center"
            >
              <input
                :id="`payment_frequency_${frequency.id}`"
                type="checkbox"
                class="h-5 w-5"
                :value="frequency.id"
                v-model="form.allowed_payment_frequency_ids"
              />
              <label :for="`payment_frequency_${frequency.id}`" class="ml-2">{{
                frequency.name
              }}</label>
            </div>
          </div>
          <validation-errors
            :errors="errorFor('allowed_payment_frequency_ids')"
          ></validation-errors>
        </div>

        <primary-button
          v-if="hasPermission('edit_referral_settings')"
          type="submit"
          class="mt-6 mx-0"
          :loading="loadingForm"
          >Guardar</primary-button
        >
      </form>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import validationErrors from "@/mixins/validationErrors";
import { is422 } from "@/utils/response";
export default {
  name: "Referral Settings",

  data() {
    return {
      form: {
        referrer_percent: 0,
        referred_percent: 0,
        welcome_days: 20,
        wallet_expiry_months: 6,
        allowed_payment_frequency_ids: [],
      },
      paymentFrequencies: [],
      loading: false,
      loadingForm: false,
    };
  },

  mixins: [validationErrors],

  computed: {
    // Same convention as the coupon form: hide the inactive "Cortesía"
    // frequency — it is not a real, chargeable cadence.
    selectablePaymentFrequencies() {
      return (this.paymentFrequencies || []).filter(
        (frequency) => Number(frequency.active) !== 0
      );
    },

    // Empty selection = the welcome coupon applies to every payment frequency.
    allPaymentFrequencies() {
      return this.form.allowed_payment_frequency_ids.length === 0;
    },
  },

  created() {
    this.$store.commit("setSelectedTab", "referralSettings");

    this.loadSettings();
  },

  methods: {
    async loadSettings() {
      this.loading = true;

      try {
        const [settings, frequencies] = await Promise.all([
          api.referralSettings.get(),
          api.resources.getFrequencies(),
        ]);

        this.paymentFrequencies = frequencies.payment_frequencies || [];
        this.form = {
          referrer_percent: Number(settings.referrer_percent),
          referred_percent: Number(settings.referred_percent),
          welcome_days: Number(settings.welcome_days),
          wallet_expiry_months: Number(settings.wallet_expiry_months),
          allowed_payment_frequency_ids: (
            settings.allowed_payment_frequency_ids || []
          ).map(Number),
        };
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    async save() {
      this.loadingForm = true;
      this.errors = {};

      try {
        const response = await api.referralSettings.update(this.form);
        this.$store.dispatch("successSnack", response.message);
      } catch (error) {
        if (is422(error)) {
          this.errors = error.response && error.response.data.errors;
        }
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingForm = false;
    },
  },
};
</script>
